import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { FiSearch, FiFilter, FiHeart } from 'react-icons/fi';
import { GiTropicalFish } from 'react-icons/gi';
import { Input, Select, Button, WaveBackground } from '../styles/GlobalStyles';
import ItemCard from '../components/ItemCard';
import ProductModal from '../components/ProductModal';

const PageHeader = styled(WaveBackground)`
  background: ${props => props.theme.gradients.wave};
  color: white;
  padding: 80px 0 60px;
  text-align: center;
`;

const HeaderContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const PageDescription = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  line-height: 1.6;
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: ${props => props.theme.colors.background};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FiltersSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.card};
  margin-bottom: 3rem;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr auto;
  gap: 1.5rem;
  align-items: end;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FilterLabel = styled.label`
  font-weight: 500;
  color: ${props => props.theme.colors.primary};
  font-size: 0.9rem;
`;

const SearchInput = styled(Input)`
  position: relative;
  padding-left: 3rem;
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.grayLight};
  pointer-events: none;
`;

const SearchContainer = styled.div`
  position: relative;
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const ResultsCount = styled.h2`
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  font-weight: 600;
`;

const SortSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.textLight};
  font-size: 1.1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  
  .empty-icon {
    font-size: 4rem;
    color: ${props => props.theme.colors.grayLight};
    margin-bottom: 1rem;
  }
  
  .empty-title {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 0.5rem;
  }
  
  .empty-description {
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 2rem;
  }
`;

const Fish = () => {
  const [fish, setFish] = useState([]);
  const [filteredFish, setFilteredFish] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [temperamentFilter, setTemperamentFilter] = useState('');
  const [dietFilter, setDietFilter] = useState('');
  const [tankSizeFilter, setTankSizeFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const navigate = useNavigate();

  const fetchFish = async (retryCount = 0) => {
    try {
      setLoading(true);
      const response = await axios.get('/api/fish');
      setFish(response.data);
    } catch (error) {
      console.error('Error fetching fish:', error);
      
      // Retry logic for Railway cold starts
      if (retryCount < 2 && (error.code === 'ECONNABORTED' || error.message === 'Request aborted')) {
        setTimeout(() => fetchFish(retryCount + 1), 2000);
        return;
      }
    } finally {
      if (retryCount === 0) {
        setLoading(false);
      }
    }
  };

  const filterAndSortFish = React.useCallback(() => {
    let filtered = [...fish];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(fishItem =>
        fishItem.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fishItem.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (fishItem.scientific_name && fishItem.scientific_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Temperament filter
    if (temperamentFilter) {
      filtered = filtered.filter(fishItem => fishItem.temperament === temperamentFilter);
    }

    // Diet filter
    if (dietFilter) {
      filtered = filtered.filter(fishItem => fishItem.diet === dietFilter);
    }

    // Tank size filter
    if (tankSizeFilter) {
      filtered = filtered.filter(fishItem => {
        if (!fishItem.tank_size_min) return false;
        const minSize = parseInt(tankSizeFilter);
        return fishItem.tank_size_min <= minSize;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'temperament':
          const temperamentOrder = { 'Friedlich': 1, 'Semi-Aggressiv': 2, 'Aggressiv': 3 };
          return (temperamentOrder[a.temperament] || 1) - (temperamentOrder[b.temperament] || 1);
        case 'tankSize':
          return (a.tank_size_min || 0) - (b.tank_size_min || 0);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'newest':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    setFilteredFish(filtered);
  }, [fish, searchTerm, temperamentFilter, dietFilter, tankSizeFilter, sortBy]);

  useEffect(() => {
    fetchFish();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterAndSortFish();
  }, [filterAndSortFish]);

  const handleContact = (fishItem) => {
    navigate('/kontakt', { state: { item: fishItem, type: 'Fisch' } });
  };

  const handleViewDetails = (fishItem) => {
    setSelectedProduct(fishItem);
  };

  const closeModal = () => {
    setSelectedProduct(null);
  };



  const clearFilters = () => {
    setSearchTerm('');
    setTemperamentFilter('');
    setDietFilter('');
    setTankSizeFilter('');
    setSortBy('newest');
  };

  if (loading) {
    return (
      <>
        <PageHeader>
          <HeaderContent>
            <PageTitle>
              <GiTropicalFish size={60} />
              Meerwasserfische
            </PageTitle>
            <PageDescription>
              Entdecken Sie unsere vielfältige Auswahl an Meerwasserfischen
            </PageDescription>
          </HeaderContent>
        </PageHeader>
        <ContentSection>
          <Container>
            <LoadingMessage>Fische werden geladen...</LoadingMessage>
          </Container>
        </ContentSection>
      </>
    );
  }

  return (
    <>
      <PageHeader>
        <HeaderContent>
          <PageTitle>
            <GiTropicalFish size={60} />
            Meerwasserfische
          </PageTitle>
          <PageDescription>
            Wenn meine Fische sich vermehren oder zu gross für mein Becken werden, 
            suche ich hier liebevolle neue Besitzer für sie.
          </PageDescription>
        </HeaderContent>
      </PageHeader>

      <ContentSection>
        <Container>
          <FiltersSection>
            <FiltersGrid>
              <FilterGroup>
                <FilterLabel>Suchen</FilterLabel>
                <SearchContainer>
                  <SearchInput
                    type="text"
                    placeholder="Nach Fischen suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <SearchIcon size={18} />
                </SearchContainer>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Temperament</FilterLabel>
                <Select
                  value={temperamentFilter}
                  onChange={(e) => setTemperamentFilter(e.target.value)}
                >
                  <option value="">Alle Arten</option>
                  <option value="Friedlich">Friedlich</option>
                  <option value="Semi-Aggressiv">Semi-Aggressiv</option>
                  <option value="Aggressiv">Aggressiv</option>
                </Select>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Ernährung</FilterLabel>
                <Select
                  value={dietFilter}
                  onChange={(e) => setDietFilter(e.target.value)}
                >
                  <option value="">Alle Arten</option>
                  <option value="Pflanzenfresser">Pflanzenfresser</option>
                  <option value="Fleischfresser">Fleischfresser</option>
                  <option value="Allesfresser">Allesfresser</option>
                </Select>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Max. Beckengrösse (L)</FilterLabel>
                <Select
                  value={tankSizeFilter}
                  onChange={(e) => setTankSizeFilter(e.target.value)}
                >
                  <option value="">Alle Grössen</option>
                  <option value="100">Bis 100L</option>
                  <option value="200">Bis 200L</option>
                  <option value="400">Bis 400L</option>
                  <option value="600">Bis 600L</option>
                  <option value="1000">Bis 1000L</option>
                </Select>
              </FilterGroup>

              <FilterGroup>
                <Button
                  outline
                  onClick={clearFilters}
                  style={{ 
                    height: '48px',
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                    padding: '0 1rem'
                  }}
                >
                  <FiFilter size={16} />
                  Zurücksetzen
                </Button>
              </FilterGroup>
            </FiltersGrid>
          </FiltersSection>

          <ResultsHeader>
            <ResultsCount>
              {filteredFish.length} Fisch{filteredFish.length !== 1 ? 'e' : ''} gefunden
            </ResultsCount>
            <SortSection>
              <FilterLabel>Sortieren nach:</FilterLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{ minWidth: '150px' }}
              >
                <option value="newest">Neueste zuerst</option>
                <option value="oldest">Älteste zuerst</option>
                <option value="name">Name A-Z</option>
                <option value="temperament">Temperament</option>
                <option value="tankSize">Beckengrösse</option>
              </Select>
            </SortSection>
          </ResultsHeader>

          {filteredFish.length === 0 ? (
            <EmptyState>
              <FiHeart className="empty-icon" />
              <div className="empty-title">
                {searchTerm || temperamentFilter || dietFilter || tankSizeFilter
                  ? 'Keine Fische gefunden'
                  : 'Noch keine Fische verfügbar'
                }
              </div>
              <div className="empty-description">
                {searchTerm || temperamentFilter || dietFilter || tankSizeFilter
                  ? 'Versuchen Sie es mit anderen Suchbegriffen oder Filtern.'
                  : 'Schauen Sie bald wieder vorbei oder kontaktieren Sie uns für spezielle Wünsche.'}
              </div>
              {(searchTerm || temperamentFilter || dietFilter || tankSizeFilter) && (
                <Button onClick={clearFilters}>
                  Zurücksetzen
                </Button>
              )}
            </EmptyState>
          ) : (
            <ItemsGrid>
              {filteredFish.map((fishItem) => (
                <ItemCard
                  key={fishItem.id}
                  item={fishItem}
                  type="fish"
                  onContact={handleContact}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </ItemsGrid>
          )}
        </Container>
      </ContentSection>
      
      {selectedProduct && (
        <ProductModal
          item={selectedProduct}
          type="fish"
          onClose={closeModal}
          onContact={handleContact}
        />
      )}
    </>
  );
};

export default Fish;

